import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FileText, CircleCheck as CheckCircle2, Clock, Loader as Loader2 } from 'lucide-react';
import { useB2BTerms, TermsSectionsView } from './B2BTermsContext';
import { useB2BUser } from './B2BUserContext';
import { formatAcceptance } from '@/lib/b2bTerms';

export function B2BTermsPage() {
  const { session } = useB2BUser();
  const { terms, showTerms, needsAcceptance, accept } = useB2BTerms();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session?.activeClientId) {
    return <div className="p-8 text-center text-sm text-gray-500">יש לבחור לקוח כדי לצפות בתנאים.</div>;
  }
  if (!terms) {
    return <div className="flex items-center justify-center gap-2 p-12 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> טוען…</div>;
  }
  if (!showTerms) return <Navigate to="/b2b/catalog" replace />;

  const handleAccept = async () => {
    setBusy(true); setError(null);
    const ok = await accept();
    if (!ok) setError('לא ניתן היה לשמור את האישור. נסו שוב.');
    setBusy(false); setAgreed(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 md:py-10 space-y-6" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center">
          <FileText className="w-5 h-5 text-sky-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">תנאים וכללים</h1>
          {terms.updated_at && (
            <p className="text-xs text-gray-500">עודכן לאחרונה: {new Date(terms.updated_at).toLocaleDateString('he-IL')}</p>
          )}
        </div>
      </div>

      {needsAcceptance ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-800">
          <Clock className="w-4 h-4 shrink-0" />
          {terms.last_accepted_at ? 'התנאים עודכנו מאז האישור האחרון — נא לאשר מחדש' : 'התנאים טרם אושרו'}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          התנאים אושרו · {formatAcceptance(terms.last_accepted_at, terms.last_accepted_by_type, terms.last_accepted_by_name)}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <TermsSectionsView terms={terms} />
      </div>

      {needsAcceptance && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                   className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
            קראתי ואני מסכים/ה
          </label>
          <div className="flex items-center gap-3">
            {error && <span className="text-xs text-red-600">{error}</span>}
            <button onClick={handleAccept} disabled={!agreed || busy}
                    className="flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium
                               rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              אני מאשר/ת
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
